import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Zap, 
  Users, 
  BarChart, 
  Shield, 
  ChevronRight, 
  Star,
  Check,
  ArrowRight,
  Menu,
  X,
  Play
} from 'lucide-react';
import { mockData } from './Mock';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set());

  // Handle scroll animations
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all sections
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Remove watermarks - simplified approach
  useEffect(() => {
    const removeWatermark = () => {
      // Only target very specific watermark elements
      const watermarkSelectors = [
        'div[style*="position: fixed"][style*="bottom"][style*="right"]',
        '*[data-testid*="watermark"]',
        '*[class*="watermark"]'
      ];
      
      watermarkSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          const text = el.textContent || '';
          if (text.toLowerCase().includes('made with')) {
            el.style.display = 'none';
          }
        });
      });
    };

    // Run after a delay to allow page to fully load
    const timer = setTimeout(removeWatermark, 3000);
    
    return () => clearTimeout(timer);
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      Zap: <Zap className="w-8 h-8" />,
      Users: <Users className="w-8 h-8" />,
      BarChart: <BarChart className="w-8 h-8" />,
      Shield: <Shield className="w-8 h-8" />
    };
    return icons[iconName] || <Zap className="w-8 h-8" />;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="text-2xl font-bold gradient-text">
              FlowAI
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 transition-colors">How it Works</a>
              <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors">Testimonials</a>
              <Button variant="outline" size="sm">Sign In</Button>
              <Button className="btn-primary" size="sm">Get Started</Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t pt-4">
              <div className="flex flex-col space-y-4">
                <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors">Features</a>
                <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 transition-colors">How it Works</a>
                <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition-colors">Pricing</a>
                <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors">Testimonials</a>
                <div className="flex flex-col space-y-2 pt-4">
                  <Button variant="outline" size="sm">Sign In</Button>
                  <Button className="btn-primary" size="sm">Get Started</Button>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient pt-20 pb-16 lg:pt-32 lg:pb-24" data-section id="hero">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className={`animate-fade-in-up ${visibleSections.has('hero') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                {mockData.hero.title}
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg">
                {mockData.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="btn-primary text-lg px-8 py-3">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button variant="outline" className="btn-secondary text-lg px-8 py-3">
                  <Play className="mr-2 w-5 h-5" />
                  Request a Demo
                </Button>
              </div>
            </div>

            {/* Right Content - Dashboard Images */}
            <div className={`relative ${visibleSections.has('hero') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}>
              <div className="relative">
                {/* Desktop Dashboard */}
                <div className="animate-float">
                  <img 
                    src={mockData.hero.dashboardImage}
                    alt="FlowAI Dashboard"
                    className="w-full rounded-2xl shadow-2xl"
                  />
                </div>
                {/* Mobile Dashboard - Positioned Overlay */}
                <div className="absolute -bottom-8 -right-8 w-48 lg:w-64 animate-float" style={{animationDelay: '1s'}}>
                  <img 
                    src={mockData.hero.mobileImage}
                    alt="FlowAI Mobile"
                    className="w-full rounded-xl shadow-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50" data-section>
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 ${visibleSections.has('features') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Powerful Features for <span className="gradient-text">Modern Teams</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to automate workflows, collaborate effectively, and scale your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.features.map((feature, index) => (
              <Card 
                key={feature.id} 
                className={`feature-card border-0 p-6 text-center ${
                  visibleSections.has('features') ? 'scroll-fade-in visible' : 'scroll-fade-in'
                }`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <CardHeader className="pb-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl gradient-primary flex items-center justify-center text-white">
                    {getIcon(feature.icon)}
                  </div>
                  <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20" data-section>
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 ${visibleSections.has('how-it-works') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Get Started in <span className="gradient-text">Three Simple Steps</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From setup to automation in minutes, not hours.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {mockData.howItWorks.map((step, index) => (
              <div 
                key={step.step}
                className={`text-center ${visibleSections.has('how-it-works') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center text-white text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-600 text-lg">{step.description}</p>
                {index < mockData.howItWorks.length - 1 && (
                  <ChevronRight className="w-8 h-8 text-gray-300 mx-auto mt-8 hidden md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50" data-section>
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 ${visibleSections.has('pricing') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Choose Your <span className="gradient-text">Perfect Plan</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start free, scale as you grow. No hidden fees, cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.pricing.map((plan, index) => (
              <Card 
                key={plan.id}
                className={`pricing-card relative p-8 ${plan.popular ? 'popular' : ''} ${
                  visibleSections.has('pricing') ? 'scroll-fade-in visible' : 'scroll-fade-in'
                }`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-blue-600 px-4 py-1">
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-8">
                  <CardTitle className={`text-2xl mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </CardTitle>
                  <div className={`mb-4 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                    <span className="text-5xl font-bold">{plan.price}</span>
                    {plan.period && <span className="text-xl">{plan.period}</span>}
                  </div>
                  <CardDescription className={plan.popular ? 'text-blue-100' : 'text-gray-600'}>
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className={`flex items-center ${plan.popular ? 'text-white' : 'text-gray-700'}`}>
                        <Check className={`w-5 h-5 mr-3 ${plan.popular ? 'text-white' : 'text-green-500'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full py-3 text-lg font-semibold ${
                      plan.popular 
                        ? 'bg-white text-blue-600 hover:bg-gray-100' 
                        : 'btn-primary'
                    }`}
                  >
                    Choose Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20" data-section>
        <div className="container mx-auto px-4">
          <div className={`text-center mb-16 ${visibleSections.has('testimonials') ? 'scroll-fade-in visible' : 'scroll-fade-in'}`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Trusted by <span className="gradient-text">Startups Worldwide</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what industry leaders are saying about FlowAI.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id}
                className={`testimonial-card p-6 ${
                  visibleSections.has('testimonials') ? 'scroll-fade-in visible' : 'scroll-fade-in'
                }`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <blockquote className="text-gray-700 mb-6 text-lg italic">
                    "{testimonial.quote}"
                  </blockquote>

                  <div className="flex items-center">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4 object-cover"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-gradient py-20 text-white" data-section id="cta">
        <div className="container mx-auto px-4 text-center">
          <div className={visibleSections.has('cta') ? 'scroll-fade-in visible' : 'scroll-fade-in'}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Start Automating Today
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
              Join thousands of teams who trust FlowAI to streamline their workflows and accelerate growth.
            </p>
            <Button 
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-3 font-semibold"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-dark py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-1">
              <div className="text-2xl font-bold gradient-text mb-4">FlowAI</div>
              <p className="text-gray-400 mb-6">
                Automating workflows and accelerating growth for startups worldwide.
              </p>
              <div className="flex space-x-4">
                {/* Social Icons */}
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <span className="text-white">f</span>
                </div>
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <span className="text-white">t</span>
                </div>
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <span className="text-white">in</span>
                </div>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Press</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Status</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>

          <Separator className="my-8 bg-gray-700" />
          
          <div className="flex flex-col md:flex-row items-center justify-between">
            <p className="text-gray-400 text-sm">
              © 2024 FlowAI. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;